export type AssignmentQuestionType = 'OpenQuestion' | 'SingleChoice' | 'TrueFalse';

export type SubmissionStatus = 'Submitted' | 'Graded' | 'Returned';

export interface StudentAssignmentQuestion {
  id: string;
  type: AssignmentQuestionType | number;
  text: string;
  maxGrade?: number | null;
  options?: string[] | null;
  order: number;
  answerText?: string | null;
  grade?: number | null;
  feedback?: string | null;
}

export interface AssignmentSubmissionSummary {
  id: string;
  submittedAt: string;
  totalGrade?: number | null;
  feedback?: string | null;
  status: SubmissionStatus | number;
  gradedAt?: string | null;
  gradedByUserName?: string | null;
}

export interface AssignmentForStudent {
  id: number;
  lessonId: number;
  lessonName: string;
  questions: StudentAssignmentQuestion[];
  totalGrade?: number | null;
  dueDate?: string | null;
  hasSubmitted: boolean;
  submission?: AssignmentSubmissionSummary | null;
}

export interface SubmitAnswerPayload {
  questionId: string;
  answerText?: string | null;
}

export interface SubmitAssignmentPayload {
  assignmentId: number;
  answers: SubmitAnswerPayload[];
}

export interface AssignmentAnswerResult {
  id: string;
  questionId: string;
  questionText: string;
  questionType: AssignmentQuestionType | number;
  questionGrade?: number | null;
  answerText?: string | null;
  grade?: number | null;
  feedback?: string | null;
  answeredAt: string;
  gradedAt?: string | null;
  isCorrect?: boolean | null;
  correctAnswer?: string | null;
}

export interface AssignmentSubmissionResult {
  id: string;
  assignmentId: number;
  studentId: string;
  studentName: string;
  studentEmail: string;
  submittedAt: string;
  totalGrade?: number | null;
  maxGrade?: number | null;
  feedback?: string | null;
  status: SubmissionStatus | number;
  gradedAt?: string | null;
  gradedByUserName?: string | null;
  answers: AssignmentAnswerResult[];
}

export const normalizeAssignmentQuestionType = (
  type: AssignmentQuestionType | number
): AssignmentQuestionType => {
  if (type === 0 || type === 'OpenQuestion') return 'OpenQuestion';
  if (type === 1 || type === 'SingleChoice') return 'SingleChoice';
  if (type === 2 || type === 'TrueFalse') return 'TrueFalse';
  return 'OpenQuestion';
};

export const normalizeSubmissionStatus = (
  status: SubmissionStatus | number
): SubmissionStatus => {
  if (status === 0 || status === 'Submitted') return 'Submitted';
  if (status === 1 || status === 'Graded') return 'Graded';
  if (status === 2 || status === 'Returned') return 'Returned';
  return 'Submitted';
};

/** True/False option labels for display (submit still uses raw option values). */
export const formatAssignmentOptionLabel = (
  text: string,
  type: AssignmentQuestionType
): string => {
  if (type !== 'TrueFalse') return text;
  const normalized = text.trim().toLowerCase().replace(/\s+/g, '');
  if (['true', 't', 'yes', '1', 'صح', 'صحيح'].includes(normalized)) return 'صح';
  if (['false', 'f', 'no', '0', 'غلط', 'خطأ', 'خطا'].includes(normalized)) return 'خطأ';
  return text;
};

export const getTrueFalseOptions = (options?: string[] | null): string[] => {
  if (options && options.length >= 2) return options;
  return ['True', 'False'];
};
